export type DoOrDumpAcquisition = "social" | "ads" | "word-of-mouth" | "not-sure";

/** Stable order for storage / display (not-sure last). */
export const DO_OR_DUMP_ACQUISITION_ORDER: DoOrDumpAcquisition[] = ["social", "ads", "word-of-mouth", "not-sure"];

export function sortAcquisitions(ids: DoOrDumpAcquisition[]): DoOrDumpAcquisition[] {
  return [...new Set(ids)].sort(
    (a, b) => DO_OR_DUMP_ACQUISITION_ORDER.indexOf(a) - DO_OR_DUMP_ACQUISITION_ORDER.indexOf(b)
  );
}
/** Stored uppercase for the verdict tile + JSON; use `verdictProseLabel` in sentences. */
export type DoOrDumpVerdict = "DO" | "DUMP";

export function verdictProseLabel(v: DoOrDumpVerdict): "Do" | "Dump" {
  return v === "DO" ? "Do" : "Dump";
}

type Tier = "strong" | "medium" | "weak";

export function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function timeTier(hours: number): Tier {
  if (hours > 20) return "strong";
  if (hours >= 10) return "medium";
  return "weak";
}

function audienceTier(followers: number): Tier {
  if (followers > 10_000) return "strong";
  if (followers >= 1_000) return "medium";
  return "weak";
}

function acquisitionTier(acq: DoOrDumpAcquisition): Tier {
  if (acq === "ads") return "strong";
  if (acq === "not-sure") return "weak";
  return "medium";
}

/** Strongest channel wins (ads > social / word of mouth); "not sure" only counts if it is the sole pick. */
function acquisitionTierFromSelections(ids: DoOrDumpAcquisition[]): Tier {
  const unique = [...new Set(ids)];
  if (unique.length === 0) return acquisitionTier("not-sure");
  const withoutUnsure = unique.filter((x) => x !== "not-sure");
  if (withoutUnsure.length === 0) return acquisitionTier("not-sure");
  if (withoutUnsure.some((x) => x === "ads")) return "strong";
  return "medium";
}

function tierPoints(t: Tier): number {
  if (t === "strong") return 34;
  if (t === "medium") return 22;
  return 11;
}

function monthlySalesCapacity(time: Tier, aud: Tier, acq: Tier): number {
  const tm = time === "strong" ? 1.45 : time === "medium" ? 1 : 0.65;
  const am = aud === "strong" ? 1.55 : aud === "medium" ? 1 : 0.55;
  const cm = acq === "strong" ? 1.3 : acq === "medium" ? 1 : 0.72;
  return Math.round(85 * tm * am * cm);
}

export type DoOrDumpDerived = {
  profitPerItem: number;
  monthlyProfitGuess: number;
  momentum: number;
  capacity: number;
  unitsFor2k: number | null;
  unitsForGoal: number | null;
  verdict: DoOrDumpVerdict;
  verdictLine: string;
  badgeClass: string;
  timeTier: Tier;
  audienceTier: Tier;
  acquisitionTier: Tier;
};

export function computeDoOrDumpDerived(input: {
  price: number;
  cost: number;
  ordersPerMonth: number;
  goalMonthly: number;
  hoursPerWeek: number;
  followers: number;
  /** How people might find you — one or more channels (tier uses the strongest signal). */
  acquisitions: DoOrDumpAcquisition[];
}): DoOrDumpDerived {
  const { price, cost, ordersPerMonth, goalMonthly, hoursPerWeek, followers, acquisitions } = input;
  const profitPerItem = price - cost;
  const monthlyProfitGuess = profitPerItem * ordersPerMonth;

  const tt = timeTier(hoursPerWeek);
  const at = audienceTier(followers);
  const ct = acquisitionTierFromSelections(acquisitions);
  const momentum = clamp(tierPoints(tt) + tierPoints(at) + tierPoints(ct), 0, 100);

  const capacity = monthlySalesCapacity(tt, at, ct);

  const unitsFor2k = profitPerItem <= 0 ? null : Math.ceil(2000 / profitPerItem);
  const unitsForGoal = profitPerItem <= 0 ? null : Math.ceil(goalMonthly / profitPerItem);

  const timeWeak = tt === "weak";
  const audWeak = at === "weak";
  const acqWeak = ct === "weak";
  const noSetup = timeWeak && audWeak && acqWeak;

  const brutalVolume = unitsForGoal != null && unitsForGoal >= 500 && timeWeak && audWeak;

  const unrealisticVolume =
    unitsForGoal != null && unitsForGoal > capacity * 3.2 && (timeWeak || audWeak);

  const stretchy = unitsForGoal != null && unitsForGoal > capacity * 1.35;

  let verdict: DoOrDumpVerdict;
  let verdictLine: string;

  if (profitPerItem <= 0) {
    verdict = "DUMP";
    verdictLine = "this isn't set up to work right now";
  } else if (noSetup) {
    verdict = "DUMP";
    verdictLine = "this isn't set up to work right now";
  } else if (brutalVolume || unrealisticVolume) {
    verdict = "DUMP";
    verdictLine = "you're depending on unrealistic volume right now";
  } else if (unitsForGoal != null && unitsForGoal <= capacity * 1.2) {
    verdict = "DO";
    verdictLine = "this could work if you actually hit the volume";
  } else {
    verdict = "DUMP";
    verdictLine = stretchy
      ? "numbers are possible, but you're relying on a lot of sales"
      : "not bad, but don't go all in yet";
  }

  const badgeClass =
    verdict === "DO"
      ? "bg-emerald-400 text-emerald-950 ring-emerald-600/30"
      : "bg-rose-500 text-white ring-rose-900/20";

  return {
    profitPerItem,
    monthlyProfitGuess,
    momentum,
    capacity,
    unitsFor2k,
    unitsForGoal,
    verdict,
    verdictLine,
    badgeClass,
    timeTier: tt,
    audienceTier: at,
    acquisitionTier: ct,
  };
}
